import type { LucideIcon } from 'lucide-react';
import { BarChart3, Building2, CalendarCheck, CalendarDays, CircleAlert, FileText, Home, Key, KeyRound, MapPin, MessageCircle, MessageSquare, MessageSquareWarning, Package, Plane, QrCode, ScanLine, ScrollText, ShoppingBag, Siren, Store, WalletCards, Users, Vote, Wrench } from 'lucide-react';


// ROLE-STRING CONVENTION (Jaime 2026-09-07): canonical role strings are the
// Spanish values the backend sends everywhere: 'propietario', 'residente',
// 'empleado', 'admin', 'super_admin'. Never compare against English aliases
// like 'employee' — they cannot match real backend data.
// Backend /api/entitlements returns feature IDs (for example `financial-reports`),
// while the navigation registry uses short product IDs. Keep this translation
// here so desktop and mobile apply the same entitlement contract.
export const ENTITLEMENT_MODULE_ALIASES: Record<string, string[]> = {
  reportes: ['reportes', 'financial-reports'],
  expenses: ['expenses'],
  // (2026-09-24, bug #129) Gastos: ProtectedRoute on /expenses uses moduleId 'gastos'
  // while the backend resolver only emits feature id 'expenses' (plans.js).
  gastos: ['gastos', 'expenses'],
  announcements: ['announcements'],
  amenities: ['amenities'],
  calendar: ['calendar'],
  qr: ['qr', 'qr-access'],
  complaints: ['complaints', 'quejas'],
  regulations: ['regulations', 'reglamentos'],
  reservations: ['reservations'],
  panic: ['panic'],
  travel: ['travel'],
  // Backend plans.js names home features with a `home-` prefix.
  tasks: ['tasks', 'home-tasks'],
  shopping: ['shopping', 'home-shopping'],
  budget: ['budget', 'home-budget'],
  vehicles: ['vehicles', 'home-vehicles'],
  pets: ['pets', 'home-pets'],
  // (2026-09-17) Equipos y Mantenimiento — backend feature id 'home-equipment'.
  equipment: ['equipment', 'home-equipment'],
  // (2026-09-19, F146) Condo equipment registry — backend feature id 'equipos-condo'.
  'equipos-condo': ['equipos-condo'],
  // (2026-10-13, Bug 1) Mantenimiento — beta module; backend feature id 'maintenance'.
  maintenance: ['maintenance'],
  // condo_free exposes these backend feature IDs; without aliases the items
  // were hidden even when the plan and domain allowed them.
  // (2026-09-07) Rentas/Rotación plan-gating: planModule ids match the planned
  // backend feature ids; backend plans must seed these for the items to show.
  rentals: ['rentals'],
  turnover: ['turnover'],
  morosos: ['morosos'],
  emergencias: ['emergencias', 'emergency', 'security'],
};

export type NavigationContext = 'home' | 'condo';
export type NavigationItem = { key: string; label: string; icon: LucideIcon; routes: Record<NavigationContext, string>; moduleId?: string; planModule?: string; homeOnly?: boolean; securityOnly?: boolean;
  /** (Jaime 2026-09-03) Ungated for ALL users/contexts/roles on BOTH menus: rendered as the fixed pinned entries (desktop IconGridSidebar footer / mobile hamburger footer). Bypasses employee, plan, role and permission gates. */ alwaysVisible?: boolean;
  /** (Jaime 2026-09-07) Hidden whenever the viewer is in employee context (backend role 'empleado'). */ hiddenInEmployeeContext?: boolean;
  /** (2026-09-07 entitlements-matrix) Role-visibility whitelist: renders only when the context carries one of these roles. Mirrors the admin gating LensDashboard applies to moduleId 'user' via isPermitted/domainForModule (LensDashboard.tsx:142) — the matrix contract carries no permission snapshot, so the role set stands in. */ roleGate?: string[];
  /** (2026-09-08) Admin-gated item: renders only for admin/sub_admin/super_admin (global role or condo membership) — mirrors AdminRoute in RoleRoute.tsx:43-48. Enforced in isRoleVisible. */ adminOnly?: boolean };
export type NavigationGroup = { title: string; items: NavigationItem[] };

// One ordered source for desktop/mobile labels. Resumen is the condo wall/dashboard destination.
export const HAMBURGER_NAVIGATION: NavigationItem[] = [
  { key: 'summary', label: 'Resumen', icon: Home, routes: { home: '/home/dashboard', condo: '/building/dashboard' }, moduleId: 'dashboard' },
  // Personal-home modules are shared with the mobile menu; hide them when a condo context is active.
  { key: 'tasks', label: 'Tareas', icon: FileText, routes: { home: '/home/tasks', condo: '/home/tasks' }, moduleId: 'home', planModule: 'tasks', homeOnly: true },
  { key: 'shopping', label: 'Compras', icon: WalletCards, routes: { home: '/home/shopping', condo: '/home/shopping' }, moduleId: 'home', planModule: 'shopping', homeOnly: true },
  { key: 'budget', label: 'Presupuesto', icon: WalletCards, routes: { home: '/home/budget', condo: '/home/budget' }, moduleId: 'home', planModule: 'budget', homeOnly: true },
  { key: 'vehicles', label: 'Vehículos', icon: KeyRound, routes: { home: '/home/cars', condo: '/home/cars' }, moduleId: 'home', planModule: 'vehicles', homeOnly: true },
  { key: 'pets', label: 'Mascotas', icon: Users, routes: { home: '/home/pets', condo: '/home/pets' }, moduleId: 'home', planModule: 'pets', homeOnly: true },
  // (2026-09-17) Equipos y Mantenimiento (F140–F142): unit equipment registry.
  // planModule keyed to 'home-equipment'; free-tier limit (5/unit) enforced by plan-gate.
  { key: 'equipment', label: 'Equipos', icon: Package, routes: { home: '/home/equipment', condo: '/home/equipment' }, moduleId: 'home', planModule: 'equipment', homeOnly: true },
  // (2026-09-19, F143-F146) Equipos del condominio (areas comunes): condo-scoped
  // registry. NOT homeOnly (condo lens only route matters); hiddenInEmployeeContext
  // intentionally NOT set — mantenimiento employees legitimately need it (F146),
  // so the item is role-gated instead: security and non-maintenance employees are
  // excluded via roleGate; the employee-context keep-list below adds this key.
  { key: 'condo-equipment', label: 'Equipos del condominio', icon: Package, routes: { home: '/building/equipment', condo: '/building/equipment' }, planModule: 'equipos-condo', roleGate: ['super_admin', 'admin', 'sub_admin', 'directivo', 'directive', 'propietario', 'resident', 'residente', 'empleado', 'maintenance_staff'] },
  // (Jaime 2026-09-12) Mi acceso is a FIXED menu tab like Mensajes/Quejas: alwaysVisible
  // (ungated, all roles/plans, both contexts) and rendered from FIXED_SIDEBAR_ITEM_KEYS.
  // Both contexts render the SAME unified page (MyQR): home -> /home/my-qr,
  // condo -> /building/resident-qr (LensDashboard now maps that case to MyQR).
  { key: 'access', label: 'Mi acceso', icon: KeyRound, routes: { home: '/home/my-qr', condo: '/building/resident-qr' }, moduleId: 'access', planModule: 'qr', alwaysVisible: true },
  // Scanner: security guards/admins verifying visitor QRs. No role filter exists in this registry,
  // so visibility keys off the qr/qr-access entitlement module plus the access-domain permission.
  // (Jaime 2026-09-07, ruling) 'payroll' / Mis Pagos REMOVED from every menu
  // (all roles): eliminated from the employee view for now; the module may be
  // revisited later. StaffPayroll page + /home/staff-payroll route stay intact.
  { key: 'verify', label: 'Escáner QR', icon: QrCode, routes: { home: '/building/verify', condo: '/building/verify' }, moduleId: 'access', planModule: 'qr', securityOnly: true },
  // (Jaime 2026-09-03) Security-lens items moved into the registry (previously
  // hard-coded in LensDashboard's security branch) so mobile mirrors desktop.
  { key: 'visitors', label: 'Registro Visitas', icon: MapPin, routes: { home: '/building/visitors', condo: '/building/visitors' }, securityOnly: true },
  { key: 'smart-access', label: 'Llave Digital', icon: Key, routes: { home: '/building/smart-access', condo: '/building/smart-access' }, securityOnly: true },
  { key: 'wall', label: 'Muro', icon: MessageSquare, routes: { home: '/home/dashboard', condo: '/building/wall' }, moduleId: 'communication' },
  // F110 DM page (spec 2026-08-29). Desktop sidebar renders 'Mensajes' as a FIXED
  // entry pinned directly above the user-info block in IconGridSidebar (see that
  // component) — deliberately NOT in the scrollable Comunidad filter below.
  // (Jaime 2026-09-03, HARD RULE) Mensajes is ALWAYS visible for ALL users on
  // BOTH menus, ungated. Rendered as a fixed pinned entry on desktop (above the
  // user-info block) and on mobile (above Cerrar sesión).
  { key: 'mensajes', label: 'Mensajes', icon: MessageCircle, routes: { home: '/mensajes', condo: '/mensajes' }, alwaysVisible: true },
  // F18 roster (packet 2026-09-24): resident-facing condo member directory with
  // self-serve block + directory opt-out. Ungated (any condo member may call the
  // roster read-only; server applies privacy filters). Distinct from the removed
  // 2026-09-08 P2-A Vecinos directory (VecinosPage.tsx stays REMOVED from UI).
  { key: 'vecinos', label: 'Vecinos', icon: Users, routes: { home: '/building/vecinos', condo: '/building/vecinos' }, alwaysVisible: true },
  { key: 'voting', label: 'Votaciones', icon: Vote, routes: { home: '/home/dashboard', condo: '/building/votings' }, moduleId: 'voting', planModule: 'voting' },
  { key: 'announcements', label: 'Anuncios', icon: FileText, routes: { home: '/building/announcements', condo: '/building/announcements' }, moduleId: 'announcement', planModule: 'announcements' },
  // P2-A Vecinos directory (2026-09-08): REMOVED from UI per Jaime ruling (2026-09-08,
  // spec ADDENDUM 2026-09-08 (c), §5 Ruling 1). Reversible: VecinosPage.tsx kept on disk;
  // restore this entry + the /condo/vecinos route in App.tsx + LensDashboard case to re-wire.
  { key: 'calendar', label: 'Calendario', icon: CalendarDays, routes: { home: '/building/calendar', condo: '/building/calendar' }, moduleId: 'calendar', planModule: 'calendar' },
  { key: 'amenities', label: 'Amenidades', icon: Home, routes: { home: '/building/amenities', condo: '/building/amenities' }, moduleId: 'amenity', planModule: 'amenities' },
  { key: 'reservations', label: 'Reservas', icon: CalendarCheck, routes: { home: '/home/reservations', condo: '/building/reservations' }, planModule: 'reservations' },
  // (Jaime 2026-09-03, HARD RULE) Quejas y Sugerencias is ALWAYS visible for ALL
  // users on BOTH menus, ungated (2026-09-05 legacy strip: all hiddenForEmployee flags removed; fixed pinned entry
  // on desktop + mobile, same placement rules as Mensajes).
  { key: 'complaints', label: 'Quejas y Sugerencias', icon: MessageSquareWarning, routes: { home: '/building/complaints', condo: '/building/complaints' }, alwaysVisible: true },
  { key: 'regulations', label: 'Reglamentos', icon: ScrollText, routes: { home: '/building/regulations', condo: '/building/regulations' }, planModule: 'regulations' },
    // (Jaime 2026-08-31) Pánico mounts under /home/panic (AppShell + LensDashboard
  // renderContent); legacy /panic and /panic-button bookmarked routes redirect.
  { key: 'panic', label: 'Pánico', icon: Siren, routes: { home: '/home/panic', condo: '/building/panic' }, planModule: 'panic' },
  { key: 'travel', label: 'Viajes', icon: Plane, routes: { home: '/home/travel', condo: '/home/travel' }, planModule: 'travel' },
  { key: 'reports', label: 'Finanzas', icon: BarChart3, routes: { home: '/building/financial-reports', condo: '/building/financial-reports' }, moduleId: 'finance', planModule: 'reportes',
    // (2026-09-28, bug-batch#1 P1) Condo-lens 'Administración' item: admin-tier only
    // (previously un-gated — any entitled resident saw Finanzas in the condo lens).
    roleGate: ['super_admin', 'admin', 'sub_admin', 'propietario'] },
  { key: 'expenses', label: 'Gastos', icon: WalletCards, routes: { home: '/building/expenses', condo: '/building/expenses' }, moduleId: 'finance', planModule: 'gastos',
    // (2026-09-24, F-15 Gap B) roleGate mirrors useModuleAccess gastos roles
    // (DEFAULT_MODULE_PERMISSIONS.gastos). planModule switched 'expenses' ->
    // 'gastos' so ENTITLEMENT_MODULE_ALIASES.gastos accepts both backend ids
    // (bug #129: plans.js emits 'expenses', ProtectedRoute checks 'gastos').
    // (2026-09-28, bug-batch#1 P1) 'resident'/'residente' removed: plain residents no
    // longer get the Gastos nav group in the Hogar lens. Propietario keeps F-15 access.
    roleGate: ['super_admin', 'admin', 'sub_admin', 'directive', 'propietario'] },
  // (Jaime 2026-08-31) Mantenimiento is condo-level: household unit employees
  // must not see this module. Unit-scoped maintenance work for staff surfaces
  // via Tareas in the home menu instead.
  // (2026-10-13, Bug 1) Mantenimiento is beta: planModule 'maintenance' is not
  // allocated by any plan tier yet, so the item only renders when the backend
  // beta/entitlement flag grants 'maintenance' to the condo/user — same
  // gating contract as condo-equipment above.
  { key: 'maintenance', label: 'Mantenimiento', icon: Wrench, routes: { home: '/home/dashboard', condo: '/building/maintenance' }, moduleId: 'maintenance', planModule: 'maintenance', roleGate: ['super_admin', 'admin', 'sub_admin', 'directivo', 'directive', 'propietario', 'maintenance_staff'] },
  { key: 'morosos', label: 'Morosos', icon: CircleAlert, routes: { home: '/building/morosos', condo: '/building/morosos' }, moduleId: 'moroso', planModule: 'morosos' },
  { key: 'emergency', label: 'Emergencias', icon: CircleAlert, routes: { home: '/building/emergency', condo: '/building/emergency' }, moduleId: 'condo', planModule: 'emergencias' },
  // (2026-09-08) Condo admin panel (/admin/condo, CondoAdminPanel.tsx). Admin-gated
  // via adminOnly (isRoleVisible), NOT plan/entitlement gated — admins must reach
  // their panel regardless of plan tier. Desktop: condo 'Administración' group.
  { key: 'condo-panel', label: 'Panel del condominio', icon: Building2, routes: { home: '/admin/condo', condo: '/admin/condo' }, adminOnly: true },
  // (2026-09-09, Jaime) Condo-level employee management tab: condo admins
  // (admin/sub_admin/super_admin) create and manage employees AT THE CONDO
  // LEVEL (unitId optional / unit_id NULL). adminOnly-gated via isRoleVisible
  // (mirrors AdminRoute), deliberately NOT plan/entitlement gated and with no
  // moduleId so no permission snapshot can hide it. Lives in the condo
  // 'Administración' group below; desktop and mobile both derive from the
  // registry + shared filterNavigation chain, so parity is automatic.
  // Condo lens routes to /building/employees (AdminRoute + AppShell +
  // LensDashboard case rendering Employees.tsx); the standalone /empleados
  // route stays as a direct fallback. The home value is inert: this key is
  // not referenced by any DESKTOP_NAVIGATION.home group.
  { key: 'empleados', label: 'Empleados', icon: Users, routes: { home: '/building/employees', condo: '/building/employees' }, adminOnly: true },
  // G1 (2026-10-01, Jaime-approved): member invite UI. adminOnly-gated like
  // 'empleados'; condo lens route /building/invites (LensDashboard case ->
  // InviteMember.tsx). No moduleId/planModule: admins must reach it.
  { key: 'invitar-usuario', label: 'Invitar usuario', icon: Users, routes: { home: '/building/invites', condo: '/building/invites' }, adminOnly: true },
  // Onboarding/frontend-self-serve (2026-10-01, Jaime-approved): admin units
  // registry — create units (POST /api/units/condo/:condoId) and (soon) link
  // standalone units via propietario link codes. adminOnly like 'empleados'.
  { key: 'unidades', label: 'Unidades', icon: Building2, routes: { home: '/building/units', condo: '/building/units' }, adminOnly: true },
  // (2026-09-09, Jaime live testing) Condo-wide QR scan audit — reads
  // GET /qr-access/audit?condoId=. AdminRoute-gated route /building/admin/qr-audit;
  // adminOnly in the registry mirrors it for desktop AND mobile menus.
  { key: 'qr-audit', label: 'Auditoría de Escaneos', icon: ScanLine, routes: { home: '/building/admin/qr-audit', condo: '/building/admin/qr-audit' }, adminOnly: true },
  { key: 'family', label: 'Familia y control', icon: Users, routes: { home: '/home/family', condo: '/home/family' }, moduleId: 'home' },
  // Mi perfil is INTENTIONALLY mobile-only (Jaime 2026-09-03 ruling): desktop has
  // no profile nav entry (identity lives in the sidebar user-info block), so this
  // item is deliberately not referenced by any DESKTOP_NAVIGATION group and is
  // appended only by MobileMenu. Keep it that way unless Jaime says otherwise.
  { key: 'profile', label: 'Mi perfil', icon: Users, routes: { home: '/home/edicion-perfil', condo: '/home/edicion-perfil' } },
];

// (2026-09-07, entitlements-matrix) The 'Usuarios y Rentas' group items were
// previously hand-built inline in DESKTOP_NAVIGATION.home, which made them
// invisible to anything iterating HAMBURGER_NAVIGATION (notably
// /admin/entitlements-matrix rows). Extracted into a named export so desktop,
// mobile and the matrix all share one source. Gating is unchanged:
// staff = role-gated admin-directory entry. (2026-09-07 21:26 UTC, Jaime
// ruling) Residents kept view-only Usuarios — SUPERSEDED 2026-09-28 (Jaime
// P1 bug report, E2E-verified leak): residents no longer see the group at all.
// Hidden for empleado.
// rentals/turnover =
// planModule entitlement gate + hiddenInEmployeeContext (Jaime 2026-09-07
// rulings). usuariosOnly Propietario Hogar lens keeps Usuarios/Rentas/Rotación
// visible (USUARIOS_ONLY_HOME_KEYS below).
export const USUARIOS_RENTAS_ITEMS: NavigationItem[] = [
  // (2026-09-03, Jaime spec): Condominio lens uses the dedicated condo-wide
  // admin route; the Hogar lens keeps the unit-scoped /home/staff-directory.
  { key: 'staff', label: 'Usuarios', icon: Users, routes: { home: '/home/staff-directory', condo: '/building/staff-directory' }, moduleId: 'user',
    // (2026-09-28, bug-batch#1 P1) Residentes NO ven el grupo admin 'Usuarios y Rentas':
    // 'resident'/'residente' removed from the whitelist (E2E leak at /home/dashboard).
    roleGate: ['super_admin', 'admin', 'sub_admin', 'propietario'] },
  // (2026-09-03, Jaime ruling) Rentas/Rotación are Propietario-visible regardless of plan: no planModule gate (the condo-lens Reservas item keeps its own gating untouched).
  // (2026-09-07, Jaime ruling) Both are HIDDEN in employee context (backend role
  // 'empleado'): role-gated via hiddenInEmployeeContext, enforced identically by
  // the shared filterNavigation chain used by LensDashboard AND MobileMenu.
  // Propietario/Admin and other roles keep them unchanged.
  // (2026-09-07) planModule 'rentals'/'turnover' added: the custom hand-built
  // "Usuarios y Rentas" group items flow through the same shared
  // filterNavigation chain (LensDashboard + MobileMenu), so isPlanVisible
  // gates them via entitlements exactly like HAMBURGER_NAVIGATION items —
  // no special-case code needed. Visible again once backend plans seed
  // the 'rentals' / 'turnover' features.
  { key: 'rentals', label: 'Rentas', icon: KeyRound, routes: { home: '/home/reservations', condo: '/home/reservations' }, moduleId: 'home', planModule: 'rentals', hiddenInEmployeeContext: true,
    // (2026-09-28, bug-batch#1 P1) Admin-group gate: residents never see Rentas.
    roleGate: ['super_admin', 'admin', 'sub_admin', 'propietario'] },
  { key: 'turnover', label: 'Rotación', icon: KeyRound, routes: { home: '/home/turnover', condo: '/home/turnover' }, moduleId: 'maintenance', planModule: 'turnover', hiddenInEmployeeContext: true,
    // (2026-09-28, bug-batch#1 P1) Admin-group gate: residents never see Rotación.
    roleGate: ['super_admin', 'admin', 'sub_admin', 'propietario'] },
];

export const DESKTOP_NAVIGATION: Record<NavigationContext, NavigationGroup[]> = {
  home: [{ title: 'Familia y Control', // (2026-09-07) 'payroll' (Mis Pagos) removed from ALL menus per Jaime.
items: HAMBURGER_NAVIGATION.filter((item) => ['summary', 'tasks', 'shopping', 'budget', 'vehicles', 'pets', 'equipment'].includes(item.key)) }, { title: 'Usuarios y Rentas', items: USUARIOS_RENTAS_ITEMS },
    // (2026-09-24, F-15 Gap B) Gastos in the Hogar lens: previously the item was
    // condo-group-only, so a paid Propietario could open /expenses by direct URL
    // but had no nav entry. plan+roleGate+entitlement gates still apply via the
    // shared filterNavigation chain (same pattern as Usuarios/Rentas).
    { title: 'Finanzas', items: HAMBURGER_NAVIGATION.filter((item) => ['expenses'].includes(item.key)) }, { title: 'Servicios', items: HAMBURGER_NAVIGATION.filter((item) => ['panic','travel'].includes(item.key)) }],
  condo: [
    { title: 'Comunidad', items: HAMBURGER_NAVIGATION.filter((item) => ['summary','vecinos','wall','announcements','calendar','amenities','verify','reservations','regulations','panic','travel','voting','maintenance','condo-equipment'].includes(item.key)) },
    { title: 'Administración', items: HAMBURGER_NAVIGATION.filter((item) => ['reports','expenses','morosos','emergency','condo-panel','empleados','qr-audit'].includes(item.key)) },
  ],
};

type EntitlementInput = { plan?: string; tier?: string; modules?: string[]; data?: EntitlementInput };

// Keep the registry tolerant of an axios-shaped response. This is deliberately
// limited to `data.modules`; permissions' `features` is a separate contract and
// must never substitute for entitlement modules.
export function normalizeEntitlement(entitlement: EntitlementInput | null): EntitlementInput | null {
  if (!entitlement) return null;
  const payload = entitlement.data && typeof entitlement.data === 'object' ? entitlement.data : entitlement;
  return {
    ...payload,
    modules: Array.isArray(payload.modules) ? payload.modules : undefined,
  };
}

export function isPlanVisible(item: NavigationItem, entitlement: EntitlementInput | null, entitlementReady = false): boolean {
  if (!item.planModule) return true;
  if (!entitlementReady || !entitlement) return false;
  const normalizedEntitlement = normalizeEntitlement(entitlement);
  if (!normalizedEntitlement) return false;
  // The entitlement endpoint is authoritative; modules may be more precise than plan/tier.
  if (Array.isArray(normalizedEntitlement.modules)) {
    const accepted = ENTITLEMENT_MODULE_ALIASES[item.planModule] || [item.planModule];
    return accepted.some((module) => normalizedEntitlement.modules!.includes(module));
  }
  // A resolver response without modules is not enough to grant access. Never
  // fall back to the legacy hardcoded plan table.
  return false;
}
// Role gate mirroring SecurityRoute in components/RoleRoute.tsx, which mirrors the
// backend canValidateQr middleware: condo-level ['admin','sub_admin','security']
// memberships plus global 'security' / 'super_admin'. Items flagged securityOnly
// (e.g. the QR scanner) must never render for regular residents.
type RoleGateUser = { role?: string | null } | null | undefined;
type RoleGateMembership = { role?: string | null };
export function isRoleVisible(item: NavigationItem, user: RoleGateUser, roles: RoleGateMembership[] | undefined): boolean {
  // (2026-09-08) Admin-gated items ('condo-panel' -> /admin/condo): same role set
  // as AdminRoute (RoleRoute.tsx:43-48) so the nav link never shows someone the
  // route itself would bounce. Global role OR active-condo membership.
  if (item.adminOnly) {
    const allowed = ['admin', 'sub_admin', 'super_admin'];
    const globalOk = allowed.includes(user?.role || '');
    const membershipOk = (roles || []).some((m) => allowed.includes(m.role || ''));
    if (!globalOk && !membershipOk) return false;
  }
  if (!item.securityOnly) return true;
  const globalAllowed = ['security', 'super_admin'].includes(user?.role || '');
  const condoScanner = (roles || []).some((m) => ['security', 'admin', 'sub_admin'].includes(m.role || ''));
  return globalAllowed || condoScanner;
}

// Employee-context gate (Jaime ruling 2026-08-31): a user is in employee context
// when their global role is staff (backend 'empleado'; audit 2026-09-07 removed
// the dead English 'employee' alias — the backend never sends it). Callers must OR this with the active-unit signal
// (OwnedUnit.assigned === true, the unit-selector-as-context-driver ruling).
export type EmployeeGateUser = { role?: string | null } | null | undefined;
export function isEmployeeContextUser(user: EmployeeGateUser): boolean {
  return ['empleado', 'maintenance_staff'].includes(user?.role || '');
}
// (Jaime 2026-08-31, TH1) Single source of truth for employee context, shared by
// LensDashboard (desktop) and MobileMenu (mobile hamburger) so the two menus can
// never drift.
// FIX (2026-09-03, mixed-role bug): the gate is scoped to the ACTIVE
// unit/context, NOT to the global user role or to "some owned unit is
// assigned". A mixed-role user (empleado in condo A + owner/resident of a
// standalone home) previously had pets/vehicles/budget hidden EVERYWHERE.
// Now: employee context = the active unit is an employee assignment
// (assigned === true) OR the active context itself carries a staff role. With
// a standalone home active the gate is FALSE, so resident features render.
// LensDashboard still ORs its own global isEmployee for the pure-employee
// lens branch; that behavior is unchanged here.
export type EmployeeGateUnit = { assigned?: boolean } | null | undefined;
export type EmployeeGateContext = { role?: string | null; subType?: string | null } | null | undefined;
const EMPLOYEE_CONTEXT_ROLES = ['empleado', 'maintenance_staff'];
export function isEmployeeContext(
  user: EmployeeGateUser,
  ownedUnits: EmployeeGateUnit[],
  activeUnit?: EmployeeGateUnit,
  activeContext?: EmployeeGateContext,
): boolean {
  if (activeUnit?.assigned === true) return true;
  const ctxRole = activeContext?.role;
  if (ctxRole && EMPLOYEE_CONTEXT_ROLES.includes(String(ctxRole))) return true;
  // (Jaime ruling 2026-09-09 12:26, F136) The CONDO SECURITY employee is a
  // user_condo_roles membership with role 'security' + sub_type 'empleado'
  // (detection pattern: src/modules/qr-access/routes.js). That context must
  // count as employee context so the reduced keep-list applies; a plain
  // 'security' membership WITHOUT the empleado sub-type stays out.
  if (String(ctxRole) === 'security' && String(activeContext?.subType || '') === 'empleado') return true;
  return false;
}

// (Jaime bug 2026-09-09 PM, Francisco Caripa dual-role leak) LENS-SCOPED
// employee gate, single source for LensDashboard + MobileMenu + home pages.
// The plain isEmployeeContext answers "is this user in an employee work
// context" but NOT "does the employee strip apply to the menu I'm rendering".
// The 2026-09-09 12:26 ruling strips Hogar items ONLY in the condo where the
// user WORKS as employee. Concretely:
//  - condo lens: unchanged isEmployeeContext (assigned unit, staff role, or
//    security membership with sub_type 'empleado' in the ACTIVE context).
//  - home lens: the strip applies ONLY to employee HOUSING (activeUnit.assigned
//    === true, i.e. the unit-selector-as-context-driver ruling). A global
//    'empleado' role or a stale condo work context must NEVER strip a user's
//    own-home menus (his standalone TH1 keeps Tareas/Compras/etc. and shows
//    Propietario, not Empleado).
export function isEmployeeContextForLens(
  navContext: 'home' | 'condo',
  user: EmployeeGateUser,
  ownedUnits: EmployeeGateUnit[],
  activeUnit?: EmployeeGateUnit,
  activeContext?: EmployeeGateContext,
): boolean {
  if (navContext === 'home') return activeUnit?.assigned === true;
  return isEmployeeContext(user, ownedUnits, activeUnit, activeContext);
}

export function isNavigationActive(pathname: string, item: NavigationItem): boolean { return Object.values(item.routes).some((route) => pathname === route || pathname.startsWith(`${route}/`)); }

// (Jaime 2026-09-03) SINGLE visibility filter shared by LensDashboard (desktop)
// and MobileMenu (mobile) so the two menus can never drift again. Root cause of
// the 2026-09-03 condo-leak bug: MobileMenu filtered the flat HAMBURGER_NAVIGATION
// list without the per-context DESKTOP_NAVIGATION group restriction, so condo
// modules whose entitlement passed leaked into the Hogar (home) menu. Mobile now
// derives from DESKTOP_NAVIGATION[context] with this exact filter chain.
// alwaysVisible items bypass every gate (Jaime hard rule: Mensajes + Quejas y
// Sugerencias for ALL users on BOTH menus).
export type NavFilterInput = {
  employeeContext: boolean;
  entitlement: EntitlementInput | null;
  entitlementReady?: boolean;
  user: RoleGateUser;
  roles?: RoleGateMembership[];
  /** Backend permission check for an item (moduleId -> domain). Omit to allow. */
  isPermitted?: (item: NavigationItem) => boolean;
  /** (2026-09-03) Non-residente Propietario Hogar lens (/api/entitlements/me data.usuariosOnly). Callers pass it ONLY for the home ('Hogar') context; the Condominio lens is untouched. */
  usuariosOnly?: boolean;
  /** Context is required for the 2026-09-08 employee condo keep-list. */
  context?: NavigationContext;
};

// (2026-09-23, QA bugs #89/#91) Standalone-home detection shared by
// LensDashboard (desktop) and MobileMenu (mobile) so the two menus can never
// drift. A standalone home is an owned unit with NO condo linkage while the
// user holds no condo context (callers AND user.condoId in). Spec: standalone
// homes = unit functions ONLY + link-to-condo CTA; the usuariosOnly strip
// (a condo-context ruling for non-residente owners) must NOT apply there.
export function isStandaloneHomeUnit(activeUnit: { condo_id?: string | null } | undefined | null, ownedUnits?: Array<{ condo_id?: string | null }> | null): boolean {
  if (activeUnit) return !activeUnit.condo_id;
  const list = ownedUnits || [];
  if (list.length === 0) return false;
  return list.every((unit) => !unit.condo_id);
}

// (2026-09-23, QA bugs #89/#91) For a standalone home the backend
// /api/entitlements/me returns usuariosOnly=true AND strips unit-scope home ids
// from `modules`, but the plan baseline `features` array still carries the
// authoritative home ids (free: home-tasks/shopping/budget/vehicles/pets/
// equipment + panic via the usuariosOnly KEEP). Unit functions are plan-gated,
// so union the plan baseline home ids back into modules for NAVIGATION
// visibility only (frontend-only view value: never persisted, never sent back).
// Condo contexts keep the raw entitlement untouched.
export function entitlementForStandaloneHome(entitlement: EntitlementInput | null): EntitlementInput | null {
  if (!entitlement) return entitlement;
  const payload = normalizeEntitlement(entitlement) as EntitlementInput & { features?: unknown };
  const planFeatures = (payload as { features?: unknown }).features;
  if (!Array.isArray(planFeatures)) return payload;
  const homeIds = planFeatures.filter((f): f is string => typeof f === 'string' && f.startsWith('home-'));
  if (homeIds.length === 0) return payload;
  return { ...payload, modules: [...new Set([...(payload.modules || []), ...homeIds])] };
}

// (2026-09-03, Jaime) When usuariosOnly is active in the Hogar lens the ONLY
// scrollable items are Usuarios (staff) plus the always-visible Mensajes and
// Quejas y Sugerencias (fixed pinned entries, bypass below). Rentas/Rotación
// stay visible per Jaime ruling 7. Resumen and all home data items
// (Tareas/Compras/Presupuesto/Vehículos/Mascotas, Mi acceso, Servicios) hide.
export const USUARIOS_ONLY_HOME_KEYS = ['staff', 'rentals', 'turnover', 'expenses'] as const;
export function filterNavigation(items: NavigationItem[], input: NavFilterInput): NavigationItem[] {
  const { employeeContext, entitlement, entitlementReady = false, user, roles, isPermitted, usuariosOnly, context } = input;
  return items.filter((item) => {
    if (item.alwaysVisible) return true;
    // Jaime ruling 2026-09-09 12:26 (F136 update): employee CONDO navigation
    // is exactly Mi acceso, Registro Visitas (the existing access-history
    // registry page — no separate Bitácora page exists), Reglamentos,
    // Escáner QR, Pánico and Emergencias. Mensajes + Quejas y Sugerencias
    // bypass via alwaysVisible above. The backend effective entitlement
    // filter is authoritative; this registry guard prevents un-plan-gated
    // entries (Resumen/Muro/Mantenimiento) from bypassing it.
    if (employeeContext && context === 'condo' && !['access', 'regulations', 'panic', 'emergency', 'verify', 'visitors', 'condo-equipment'].includes(item.key)) return false;
    // (Jaime ruling 2026-09-09 12:26, F136) Employee-only users in the Hogar
    // (home) lens do NOT get the home-section modules (Tareas/Compras/
    // Presupuesto/Vehículos/Mascotas). Dual-role users are unaffected: the
    // shared isEmployeeContext gate is scoped to the ACTIVE context, so a
    // standalone resident home keeps these items.
    if (employeeContext && context === 'home' && ['budget', 'vehicles', 'pets'].includes(item.key)) return false;
    // (2026-09-03) usuariosOnly Hogar lens: whitelist + fixed pinned entries only.
    if (usuariosOnly && !(USUARIOS_ONLY_HOME_KEYS as readonly string[]).includes(item.key)) return false;
    // (2026-09-07, Jaime) Rentas/Rotación-style items hidden in employee context.
    if (item.hiddenInEmployeeContext && employeeContext) return false;
    // (2026-09-19, F146) Enforce roleGate in the shared menu filter too: previously
    // only EntitlementsMatrixPage checked it, so role-gated items leaked into the
    // desktop/mobile menus for excluded roles. Context role set = global role +
    // condo memberships (same ctx.roleSet logic as EntitlementsMatrixPage.tsx:323).
    if (item.roleGate) {
      const ctxRoles = [user?.role || '', ...(roles || []).map((m) => m.role || '')];
      if (!item.roleGate.some((r) => ctxRoles.includes(r))) return false;
    }
    const planVisible = isPlanVisible(item, entitlement, entitlementReady);
    if (!planVisible) return false;
    if (!isRoleVisible(item, user, roles)) return false;
    if (isPermitted && !isPermitted(item)) {
      // (2026-09-24, QA bug #130) Paid RESIDENTE of a condo-linked unit: the
      // backend /api/me/permissions snapshot comes back EMPTY for plain
      // residente memberships (verified live: {} for qa-residente-paid, while
      // entitlements /me modules carry the full home-* set). The standalone
      // owner (qa-prop-standalone) gets permissions.home=true, which is why
      // only the condo-residente persona lost every Hogar item. For the HOME
      // lens unit functions (moduleId 'home': Tareas/Compras/Presupuesto/
      // Vehiculos/Mascotas/Equipos), the plan/entitlement gate above is
      // authoritative — a missing coarse 'home' domain permission must not
      // double-gate residency/plan-derived items on top of it. Plan LIMITS
      // semantics are untouched: isPlanVisible still enforces the entitlement
      // modules (budget/vehicles/pets/equipment...), and this carve-out applies
      // ONLY in the home context so condo-lens admin domains (finance, moroso,
      // user...) keep their permission gate exactly as before.
      if (context === 'home' && item.moduleId === 'home') return true;
      // (2026-09-24, F-15 Gap B) Gastos in the Hogar lens: access control for
      // /expenses is ProtectedRoute moduleId 'gastos' (useModuleAccess: role +
      // entitlement). The coarse /me/permissions 'finance' domain is documented
      // empty for propietario/residente personas (bug #130 above), so it must
      // not double-gate the nav entry on top of plan+roleGate. Condo lens keeps
      // the permission gate exactly as before.
      if (context === 'home' && item.key === 'expenses') return true;
      return false;
    }
    return true;
  });
}

// (Jaime 2026-09-03) Services-lens group moved into the registry so any future
// mobile services entry derives from the same source. Desktop-only lens today:
// the hamburger has no services context, so these are NOT part of mobile parity.
export const SERVICES_NAVIGATION: NavigationGroup[] = [{ title: 'Directorio', items: [
  { key: 'services-dashboard', label: 'Marketplace', icon: Wrench, routes: { home: '/services/dashboard', condo: '/services/dashboard' } },
  { key: 'services-suppliers', label: 'Proveedores', icon: Store, routes: { home: '/services/suppliers', condo: '/services/suppliers' } },
  { key: 'services-promotions', label: 'Promociones', icon: ShoppingBag, routes: { home: '/services/promotions', condo: '/services/promotions' } },
]}];

// Fixed pinned entries (desktop IconGridSidebar footer + mobile hamburger footer),
// in placement order. Registry-driven since 2026-09-03.
export const FIXED_SIDEBAR_ITEM_KEYS = ['mensajes', 'complaints', 'access'] as const;

// ── I18N STAGE 2b (2026-09-23) ────────────────────────────────────────────────
// Registry labels stay canonical Spanish data (they also feed the entitlements
// matrix and legacy consumers). RENDER sites resolve user-visible menu text
// through the 'common' namespace via these helpers — desktop (IconGridSidebar
// via LensDashboard) and mobile (MobileMenu) translate from ONE source.
// Every key below MUST exist in src/locales/*/common.json (es-MX is the base).
export const NAV_LABEL_KEYS: Record<string, string> = {
  summary: 'nav.summary',
  tasks: 'nav.tasks',
  shopping: 'nav.shopping',
  budget: 'nav.budget',
  vehicles: 'nav.vehicles',
  pets: 'nav.pets',
  equipment: 'nav.equipment',
  'condo-equipment': 'nav.condoEquipment',
  access: 'nav.access',
  verify: 'nav.verify',
  visitors: 'nav.visitors',
  'smart-access': 'nav.smartAccess',
  wall: 'nav.wall',
  mensajes: 'nav.mensajes',
  voting: 'nav.voting',
  announcements: 'nav.announcements',
  calendar: 'nav.calendar',
  amenities: 'nav.amenities',
  reservations: 'nav.reservations',
  complaints: 'nav.complaints',
  regulations: 'nav.regulations',
  panic: 'nav.panic',
  travel: 'nav.travel',
  reports: 'nav.reports',
  expenses: 'nav.expenses',
  maintenance: 'nav.maintenance',
  morosos: 'nav.morosos',
  emergency: 'nav.emergency',
  'condo-panel': 'nav.condoPanel',
  empleados: 'nav.employees',
  'qr-audit': 'nav.qrAudit',
  family: 'nav.family',
  profile: 'nav.profile',
  staff: 'nav.staff',
  rentals: 'nav.rentals',
  turnover: 'nav.turnover',
  'services-dashboard': 'nav.servicesDashboard',
  'services-suppliers': 'nav.servicesSuppliers',
  'services-promotions': 'nav.servicesPromotions',
};

export const NAV_GROUP_TITLE_KEYS: Record<string, string> = {
  'Finanzas': 'nav.reports',
  'Familia y Control': 'nav.group.familyControl',
  'Usuarios y Rentas': 'nav.group.usersRentals',
  'Servicios': 'nav.group.services',
  'Comunidad': 'nav.group.community',
  'Administración': 'nav.group.administration',
  'Directorio': 'nav.group.directory',
  'Seguridad': 'nav.group.security',
};

/** i18n key for a registry item key (falls back to the raw key → caller passes defaultValue = registry label). */
export function navLabelKey(key: string): string {
  return NAV_LABEL_KEYS[key] || key;
}

/** i18n key for a registry group title (unmapped titles fall back to the raw string). */
export function navGroupTitleKey(title: string): string {
  return NAV_GROUP_TITLE_KEYS[title] || title;
}
